import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_BUTTON_BLOCKS_QUERY,
  SINGLE_BUTTON_BLOCK_QUERY,
  CREATE_BUTTON_BLOCK_MUTATION,
  UPDATE_BUTTON_BLOCK_MUTATION,
  DELETE_BUTTON_BLOCK_MUTATION,
  FORCE_DELETE_BUTTON_BLOCK_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsButtonBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_BUTTON_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.buttonBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_BUTTON_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.buttonBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_BUTTON_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.buttonBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_BUTTON_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.buttonBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_BUTTON_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.buttonBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_BUTTON_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.buttonBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_BUTTON_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsButtonBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_BUTTON_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsButtonBlock
    })
  },
}

export default cmsButtonBlockRepository
